import {Timezone} from 'common/constants';
import {ProjectFeatureFlag} from 'common/report/constants';
import {TimePeriodSettings} from 'common/report/stores/types';
import {Translatable} from 'common/i18n';
import {
  endOfFiscalYear,
  endOfQuarter,
  endOfWeek,
  getCurrentMomentAsOf,
  isoWeekdayFromSettingWeekday,
  SettingWeekday,
  startOfFiscalYear,
  startOfQuarter,
  startOfWeek,
} from 'common/util/date';
import {translatable} from 'common/i18n/scope';

import {DateRangeAttributes, DateRangeType, TimeUnit} from './types';
import translationKeys from './index.i18n';

export enum PresetDateRange {
  Today = `today`,
  Yesterday = `yesterday`,
  Last1Day = `last_1_day`,
  Last96Hours = `last_96_hours`,
  Last7Days = `last_7_days`,
  Last30Days = `last_30_days`,
  Last3Months = `last_3_months`,
  Last24Weeks = `last_24_weeks`,
  Last6Months = `last_6_months`,
  Last12Months = `last_12_months`,
  WeekToDate = `week_to_date`,
  MonthToDate = `month_to_date`,
  QuarterToDate = `quarter_to_date`,
  YearToDate = `year_to_date`,
  PreviousWeek = `previous_week`,
  PreviousMonth = `previous_month`,
  PreviousQuarter = `previous_quarter`,
  PreviousYear = `previous_year`,
}

export const SHOW_LAST_X_DAYS_PRESET_DATE_RANGE = {
  [ProjectFeatureFlag.ShowLast1Day]: PresetDateRange.Last1Day,
  [ProjectFeatureFlag.ShowLast7Days]: PresetDateRange.Last7Days,
};

export const TIME_UNIT_DISPLAY_NAMES: {[key in TimeUnit]: string} = {
  [TimeUnit.Second]: `seconds`,
  [TimeUnit.Minute]: `minutes`,
  [TimeUnit.Hour]: `hours`,
  [TimeUnit.Day]: `days`,
  [TimeUnit.Week]: `weeks`,
  [TimeUnit.Month]: `months`,
  [TimeUnit.Quarter]: `quarters`,
  [TimeUnit.Year]: `years`,
  [TimeUnit.Session]: `sessions`,
  // Not sure about those two. added special time unit into TimeUnit because it's actually being used
  [TimeUnit.DayOfWeek]: `days`,
  [TimeUnit.HourOfDay]: `hours`,
};

export const DATE_RANGE_TYPE_DISPLAY_NAMES: {[key in DateRangeType]: Translatable} = {
  [DateRangeType.Between]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.between`)),
  [DateRangeType.NotBetween]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.not-between`)),
  [DateRangeType.On]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.on`)),
  [DateRangeType.NotOn]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.not-on`)),
  [DateRangeType.RelativeAfter]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.relative-after`)),
  [DateRangeType.RelativeBefore]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.relative-before`)),
  [DateRangeType.Since]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.since`)),
  [DateRangeType.StartsOn]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.starts-on`)),
  [DateRangeType.EndsOn]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.ends-on`)),
  [DateRangeType.RelativeFuture]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.relative-future`)),
  [DateRangeType.NotRelativeAfter]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.type.not-relative-after`),
  ),
  [DateRangeType.Before]: translatable(translationKeys, ({t}) => t(`reports.date-range.type.before`)),
};

export const PERIOD_PRESET_UNITS: TimeUnit[] = [
  TimeUnit.Day,
  TimeUnit.Week,
  TimeUnit.Month,
  TimeUnit.Quarter,
  TimeUnit.Year,
];

export const NOW = `$now`;
export const TODAY = `$today`;

export const END_OF_TODAY = `$end_of_current_day`;
export const END_OF_YESTERDAY = `$end_of_previous_day`;
export const END_OF_PREVIOUS_WEEK = `$end_of_previous_week`;
export const END_OF_PREVIOUS_MONTH = `$end_of_previous_month`;
export const END_OF_PREVIOUS_QUARTER = `$end_of_previous_quarter`;
export const END_OF_PREVIOUS_YEAR = `$end_of_previous_year`;

export const START_OF_WEEK = `$start_of_week`;
export const START_OF_MONTH = `$start_of_month`;
export const START_OF_TODAY = `$start_of_current_day`;
export const START_OF_QUARTER = `$start_of_quarter`;
export const START_OF_YEAR = `$start_of_year`;
export const CURRENT_PERIOD_START = [START_OF_TODAY, START_OF_WEEK, START_OF_MONTH, START_OF_QUARTER, START_OF_YEAR];
export const START_OF_YESTERDAY = `$start_of_previous_day`;
export const START_OF_PREVIOUS_WEEK = `$start_of_previous_week`;
export const START_OF_PREVIOUS_MONTH = `$start_of_previous_month`;
export const START_OF_PREVIOUS_QUARTER = `$start_of_previous_quarter`;
export const START_OF_PREVIOUS_YEAR = `$start_of_previous_year`;

export const END_OF_DATE_STRINGS = [
  END_OF_TODAY,
  END_OF_YESTERDAY,
  END_OF_PREVIOUS_WEEK,
  END_OF_PREVIOUS_MONTH,
  END_OF_PREVIOUS_QUARTER,
  END_OF_PREVIOUS_YEAR,
] as const;

export const START_OF_DATE_STRINGS = [
  ...CURRENT_PERIOD_START,
  START_OF_YESTERDAY,
  START_OF_PREVIOUS_WEEK,
  START_OF_PREVIOUS_MONTH,
  START_OF_PREVIOUS_QUARTER,
  START_OF_PREVIOUS_YEAR,
] as const;
const DEPRECATED_SPECIAL_DATE_STRINGS = [NOW, TODAY] as const;
export type SpecialDateString =
  | (typeof END_OF_DATE_STRINGS)[number]
  | (typeof START_OF_DATE_STRINGS)[number]
  | (typeof DEPRECATED_SPECIAL_DATE_STRINGS)[number];

export const SPECIAL_DATE_RESOLVERS: {
  [Key in SpecialDateString]: (
    asOfDate: Date | null,
    projectTz: Timezone,
    timePeriodSettings: TimePeriodSettings,
  ) => Date;
} = {
  [START_OF_YESTERDAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).subtract(1, `days`).startOf(`day`).toDate(),
  [END_OF_YESTERDAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).subtract(1, `days`).endOf(`day`).toDate(),
  [START_OF_TODAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).startOf(`day`).toDate(),
  [END_OF_TODAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).endOf(`day`).toDate(),
  [TODAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).startOf(`day`).toDate(),
  [NOW]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).toDate(),
  [START_OF_WEEK]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfWeek(
      curMoment,
      projectTz,
      isoWeekdayFromSettingWeekday(timePeriodSettings.weekStartDay as SettingWeekday),
    );
  },
  [START_OF_MONTH]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).startOf(`month`).toDate(),
  [START_OF_QUARTER]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfQuarter(curMoment, projectTz, timePeriodSettings.quarterStartMonth);
  },
  [START_OF_YEAR]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfFiscalYear(curMoment, projectTz, timePeriodSettings.quarterStartMonth);
  },
  [START_OF_PREVIOUS_WEEK]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfWeek(
      curMoment.subtract(7, `days`),
      projectTz,
      isoWeekdayFromSettingWeekday(timePeriodSettings.weekStartDay as SettingWeekday),
    );
  },
  [START_OF_PREVIOUS_MONTH]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).subtract(1, `month`).startOf(`month`).toDate(),
  [START_OF_PREVIOUS_QUARTER]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfQuarter(curMoment.subtract(3, `month`), projectTz, timePeriodSettings.quarterStartMonth);
  },
  [START_OF_PREVIOUS_YEAR]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfFiscalYear(curMoment.subtract(1, `year`), projectTz, timePeriodSettings.quarterStartMonth);
  },
  [END_OF_PREVIOUS_WEEK]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return endOfWeek(
      curMoment.subtract(7, `days`),
      projectTz,
      isoWeekdayFromSettingWeekday(timePeriodSettings.weekStartDay as SettingWeekday),
    );
  },
  [END_OF_PREVIOUS_MONTH]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).subtract(1, `month`).endOf(`month`).toDate(),
  [END_OF_PREVIOUS_QUARTER]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return endOfQuarter(curMoment.subtract(3, `month`), projectTz, timePeriodSettings.quarterStartMonth);
  },
  [END_OF_PREVIOUS_YEAR]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return endOfFiscalYear(curMoment.subtract(1, `year`), projectTz, timePeriodSettings.quarterStartMonth);
  },
};

// Note: we use the same display name for a presets corresponding start-of and end-of strings,
// independent of whethere they are used as the from or to dates.
// This is a deliberate choice for conciseness.
// Despite being technically schematically valid, we will not allow the generation of end-of strings as the fromDate or
// start-of strings as the toDate in Copilot or the UX. This is because we do not support exclusive bounds and when time is
// disable and with certain unit combination, we would produce unintuive results, such as
// `from: END_OF_YESTERDAY`, including the entire date of Yesterday. For this reason, we will not be adding
// a special `end of yesterday` display name, etc.
export const SPECIAL_DATE_DISPLAY_NAMES: {[key in SpecialDateString]: Translatable} = {
  [NOW]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.today`)),
  [TODAY]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.today`)),
  [START_OF_TODAY]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.today`)),
  [END_OF_TODAY]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.today`)),
  [START_OF_YESTERDAY]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.yesterday`)),
  [END_OF_YESTERDAY]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.yesterday`)),
  [START_OF_WEEK]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.period-to-date.current-week`)),
  [START_OF_MONTH]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.current-month`),
  ),
  [START_OF_QUARTER]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.current-quarter`),
  ),
  [START_OF_YEAR]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.period-to-date.current-year`)),
  [START_OF_PREVIOUS_WEEK]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.week`),
  ),
  [END_OF_PREVIOUS_WEEK]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.previous-period.week`)),
  [START_OF_PREVIOUS_MONTH]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.month`),
  ),
  [END_OF_PREVIOUS_MONTH]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.month`),
  ),
  [START_OF_PREVIOUS_QUARTER]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.quarter`),
  ),
  [END_OF_PREVIOUS_QUARTER]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.quarter`),
  ),
  [START_OF_PREVIOUS_YEAR]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.year`),
  ),
  [END_OF_PREVIOUS_YEAR]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.previous-period.year`)),
};

export const PRESET_DATE_RANGE_DISPLAY_NAMES: {[key in PresetDateRange]: Translatable} = {
  [PresetDateRange.Today]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.today`)),
  [PresetDateRange.Yesterday]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.yesterday`)),
  [PresetDateRange.Last1Day]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.description.1-day`)),
  [PresetDateRange.Last96Hours]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.description.96-hours`),
  ),
  [PresetDateRange.Last7Days]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.description.7-days`),
  ),
  [PresetDateRange.Last30Days]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.description.30-days`),
  ),
  [PresetDateRange.Last3Months]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.description.3-months`),
  ),
  [PresetDateRange.Last24Weeks]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.description.24-weeks`),
  ),
  [PresetDateRange.Last6Months]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.description.6-months`),
  ),
  [PresetDateRange.Last12Months]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.description.12-months`),
  ),
  [PresetDateRange.WeekToDate]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.week`),
  ),
  [PresetDateRange.MonthToDate]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.month`),
  ),
  [PresetDateRange.QuarterToDate]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.quarter`),
  ),
  [PresetDateRange.YearToDate]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.year`),
  ),
  [PresetDateRange.PreviousWeek]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.week`),
  ),
  [PresetDateRange.PreviousMonth]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.month`),
  ),
  [PresetDateRange.PreviousQuarter]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.quarter`),
  ),
  [PresetDateRange.PreviousYear]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.year`),
  ),
};

export const PRESET_DATE_RANGE_SHORT_DISPLAY_NAMES: {[key in PresetDateRange]: Translatable} = {
  [PresetDateRange.Today]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.today`)),
  [PresetDateRange.Yesterday]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.yesterday`)),
  [PresetDateRange.Last1Day]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.1-day`)),
  [PresetDateRange.Last96Hours]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.96-hours`)),
  [PresetDateRange.Last7Days]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.7-days`)),
  [PresetDateRange.Last30Days]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.30-days`)),
  [PresetDateRange.Last3Months]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.3-months`)),
  [PresetDateRange.Last24Weeks]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.24-weeks`)),
  [PresetDateRange.Last6Months]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.6-months`)),
  [PresetDateRange.Last12Months]: translatable(translationKeys, ({t}) => t(`reports.date-range.presets.12-months`)),
  [PresetDateRange.WeekToDate]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.week`),
  ),
  [PresetDateRange.MonthToDate]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.month`),
  ),
  [PresetDateRange.QuarterToDate]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.quarter`),
  ),
  [PresetDateRange.YearToDate]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.period-to-date.year`),
  ),
  [PresetDateRange.PreviousWeek]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.week`),
  ),
  [PresetDateRange.PreviousMonth]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.month`),
  ),
  [PresetDateRange.PreviousQuarter]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.quarter`),
  ),
  [PresetDateRange.PreviousYear]: translatable(translationKeys, ({t}) =>
    t(`reports.date-range.presets.previous-period.year`),
  ),
};

export const PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS: {[key in PresetDateRange]: DateRangeAttributes} = {
  [PresetDateRange.Today]: {
    from: START_OF_TODAY,
    to: END_OF_TODAY,
    type: DateRangeType.Between,
  },
  [PresetDateRange.Yesterday]: {
    from: START_OF_YESTERDAY,
    to: END_OF_YESTERDAY,
    type: DateRangeType.Between,
  },
  [PresetDateRange.Last1Day]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 1,
    },
  },
  [PresetDateRange.Last96Hours]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Hour,
      value: 96,
    },
  },
  [PresetDateRange.Last7Days]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 7,
    },
  },
  [PresetDateRange.Last30Days]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 30,
    },
  },
  [PresetDateRange.Last3Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 3,
    },
  },
  [PresetDateRange.Last24Weeks]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Week,
      value: 24,
    },
  },
  [PresetDateRange.Last6Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 6,
    },
  },
  [PresetDateRange.Last12Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 12,
    },
  },
  [PresetDateRange.WeekToDate]: {
    from: START_OF_WEEK,
    type: DateRangeType.Since,
  },
  [PresetDateRange.MonthToDate]: {
    from: START_OF_MONTH,
    type: DateRangeType.Since,
  },
  [PresetDateRange.QuarterToDate]: {
    from: START_OF_QUARTER,
    type: DateRangeType.Since,
  },
  [PresetDateRange.YearToDate]: {
    from: START_OF_YEAR,
    type: DateRangeType.Since,
  },
  [PresetDateRange.PreviousWeek]: {
    from: START_OF_PREVIOUS_WEEK,
    to: END_OF_PREVIOUS_WEEK,
    type: DateRangeType.Between,
  },
  [PresetDateRange.PreviousMonth]: {
    from: START_OF_PREVIOUS_MONTH,
    to: END_OF_PREVIOUS_MONTH,
    type: DateRangeType.Between,
  },
  [PresetDateRange.PreviousQuarter]: {
    from: START_OF_PREVIOUS_QUARTER,
    to: END_OF_PREVIOUS_QUARTER,
    type: DateRangeType.Between,
  },
  [PresetDateRange.PreviousYear]: {
    from: START_OF_PREVIOUS_YEAR,
    to: END_OF_PREVIOUS_YEAR,
    type: DateRangeType.Between,
  },
};

// Legacy alternative schema representations for Today preset used in some surfaces
// Do not extend these further. We will add guarantees for ai generated date ranges
// to normalize to the expected preset format.
const SINCE_TODAY = {
  from: START_OF_TODAY,
  type: DateRangeType.Since,
};
const SINCE_TODAY_TO_NOW = {
  from: START_OF_TODAY,
  to: NOW,
  type: DateRangeType.Since,
};
const PERIOD_TO_DATE: DateRangeAttributes[] = [
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Today],
  SINCE_TODAY,
  SINCE_TODAY_TO_NOW,
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.WeekToDate],
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.MonthToDate],
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.QuarterToDate],
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.YearToDate],
];
const PREVIOUS_PERIOD_PRESETS: DateRangeAttributes[] = [
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Yesterday],
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousWeek],
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousMonth],
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousQuarter],
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousYear],
];

export const TIME_COMPARISON_OPTIONS = {
  PREVIOUS_DAY: `previous_day`,
  PREVIOUS_7_DAYS: `previous_7_days`,
  PREVIOUS_30_DAYS: `previous_30_days`,
  PREVIOUS_3_MONTHS: `previous_3_months`,
  PREVIOUS_WEEK: `previous_week`,
  PREVIOUS_MONTH: `previous_month`,
  PREVIOUS_QUARTER: `previous_quarter`,
  PREVIOUS_YEAR: `previous_year`,
  CUSTOM: `custom`,
};

type ComparisonTimeUnit =
  | typeof TimeUnit.Day
  | typeof TimeUnit.Week
  | typeof TimeUnit.Month
  | typeof TimeUnit.Quarter
  | typeof TimeUnit.Year;
export const TIME_COMPARISON_OPTION_LABELS: {[key in ComparisonTimeUnit]: Translatable} = {
  [TimeUnit.Day]: translatable(translationKeys, ({t}) => t(`reports.lift-comparison.previous-period.day`)),
  [TimeUnit.Week]: translatable(translationKeys, ({t}) => t(`reports.lift-comparison.previous-period.week`)),
  [TimeUnit.Month]: translatable(translationKeys, ({t}) => t(`reports.lift-comparison.previous-period.month`)),
  [TimeUnit.Quarter]: translatable(translationKeys, ({t}) => t(`reports.lift-comparison.previous-period.quarter`)),
  [TimeUnit.Year]: translatable(translationKeys, ({t}) => t(`reports.lift-comparison.previous-period.year`)),
};

/**
 * matches to a preset without factoring in exclusion offset
 */
function matchesPreset(preset: DateRangeAttributes, dateRange: DateRangeAttributes | undefined): boolean {
  return (
    preset.from === dateRange?.from &&
    preset.to === dateRange?.to &&
    preset.type === dateRange?.type &&
    preset.window?.unit === dateRange?.window?.unit &&
    preset.window?.value === dateRange?.window?.value
  );
}

export function isPreviousPeriodPreset(dateRange: DateRangeAttributes | undefined): boolean {
  return PREVIOUS_PERIOD_PRESETS.some((preset) => matchesPreset(preset, dateRange));
}

export function isPeriodToDate(dateRange: DateRangeAttributes | undefined): boolean {
  return PERIOD_TO_DATE.some((preset) => matchesPreset(preset, dateRange));
}

export function getPresetDateRangeFromAttributes(
  dateRange: DateRangeAttributes | undefined,
): PresetDateRange | undefined {
  for (const [key, preset] of Object.entries(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS)) {
    if (matchesPreset(preset, dateRange)) {
      return key as PresetDateRange;
    }
  }
  // Alternative schema representations for Today preset used in some surfaces
  if (matchesPreset(SINCE_TODAY, dateRange) || matchesPreset(SINCE_TODAY_TO_NOW, dateRange)) {
    return PresetDateRange.Today;
  }
  return undefined;
}

export function unitToPeriodToDateAttributes(unit: TimeUnit): DateRangeAttributes {
  switch (unit) {
    case TimeUnit.Day:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Today];
    case TimeUnit.Week:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.WeekToDate];
    case TimeUnit.Month:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.MonthToDate];
    case TimeUnit.Quarter:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.QuarterToDate];
    case TimeUnit.Year:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.YearToDate];
    default:
      throw new Error(`unsupported time unit option for period to date`);
  }
}

export function unitToPreviousPeriodAttributes(unit: TimeUnit): DateRangeAttributes {
  switch (unit) {
    case TimeUnit.Day:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Yesterday];
    case TimeUnit.Week:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousWeek];
    case TimeUnit.Month:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousMonth];
    case TimeUnit.Quarter:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousQuarter];
    case TimeUnit.Year:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousYear];
    default:
      throw new Error(`unsupported time unit option for previous periods`);
  }
}
